import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axios.config';
import { logoutUser } from './authSlice';
import { API_URL } from '../../lib/api-client';
import { logger } from '../../utils/logger';

interface Message {
  _id: string;
  senderId: string;  // Changed from conversationId to match backend
  content: string;
  messageType: string;
  isRead: boolean;
  createdAt: string;
}

interface Conversation {
  _id: string;
  participants: Array<{
    _id: string;
    firstName: string;
    lastName: string;
    profile?: {
      profilePhoto?: string;
      age?: number;
    };
    lastSeen?: string;
    isOnline?: boolean;
  }>;
  lastMessage?: {  // Made optional to handle new chats without messages
    content: string;
    createdAt: string;
    sender: string;
  };
  unreadCount: number;
  updatedAt: string;
  initiatedBy?: string; // ID of user who sent the first interest
  chatRestriction?: {
    restrictedUserId?: string; // User who cannot reply (if any)
    reason?: string;
  };
  // Interest-related fields
  interestStatus?: 'pending' | 'accepted' | 'declined';
  interestSender?: string; // User ID who sent the interest
  interestRecipient?: string; // User ID who received the interest
  interestMessage?: string; // Optional message with the interest
  interestAcceptedAt?: string; // Timestamp when interest was accepted
}

interface ChatState {
  conversations: Conversation[];
  currentConversation: Conversation | null;
  messages: Message[];
  loading: boolean;
  messagesLoading: boolean;
  error: string | null;
  onlineUsers: string[];
  userPresence: Record<string, { isOnline: boolean; lastSeen?: string }>;
  lastUpdate: number; // Add timestamp to force re-renders
  blockedUsers: string[]; // List of blocked user IDs
  lastFetched: number | null; // Cache timestamp
  cacheValid: boolean; // Cache validity flag
  // Pagination for messages
  hasMoreMessages: boolean; // Are there more messages to load?
  messagesPage: number; // Current page number
  loadingOlderMessages: boolean; // Loading older messages (different from initial load)
}

const initialState: ChatState = {
  conversations: [],
  currentConversation: null,
  messages: [],
  loading: false,
  messagesLoading: false,
  error: null,
  onlineUsers: [],
  userPresence: {},
  lastUpdate: Date.now(),
  blockedUsers: [],
  lastFetched: null,
  cacheValid: false,
  hasMoreMessages: true,
  messagesPage: 1,
  loadingOlderMessages: false,
};

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

// Async thunks
export const fetchConversations = createAsyncThunk(
  'chat/fetchConversations',
  async (forceRefresh: boolean = false, { getState, rejectWithValue }) => {
    try {
      const state = getState() as any;
      const chatState = state.chat;

      // Check if cache is still valid
      if (
        !forceRefresh &&
        chatState.cacheValid &&
        chatState.lastFetched &&
        Date.now() - chatState.lastFetched < CACHE_DURATION
      ) {
        return null; // Return null to indicate cached data is being used
      }

      const { data } = await axiosInstance.get(`${API_URL}/chats`);
      
      if (data.success) {
        logger.log('✅ Redux: Conversations fetched successfully:', data.data.chats?.length || 0);
        
        // Return chats directly - profile data is already populated by the backend
        return data.data.chats || [];
      }
      return rejectWithValue('Failed to fetch conversations');
    } catch (error: any) {
      // Silently handle errors when backend not accessible
      // console.error('❌ Redux: Fetch conversations error:', error.response?.data?.message);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch conversations');
    }
  }
);

export const fetchMessages = createAsyncThunk(
  'chat/fetchMessages',
  async ({ conversationId, page = 1, limit = 50 }: { conversationId: string; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      logger.log('💬 Redux: Fetching messages for conversation:', conversationId, 'Page:', page, 'Limit:', limit);
      const { data } = await axiosInstance.get(`${API_URL}/chats/${conversationId}/messages?page=${page}&limit=${limit}`);
      
      if (data.success) {
        logger.log('✅ Redux: Messages fetched successfully:', data.data.chat?.messages?.length || 0);
        return { 
          messages: data.data.chat?.messages || [], 
          hasMore: data.data.hasMore !== false, // Assume true if not specified
          page 
        };
      }
      return rejectWithValue('Failed to fetch messages');
    } catch (error: any) {
      // Silently handle errors when backend not accessible (demo mode)
      // console.error('❌ Redux: Fetch messages error:', error.response?.data?.message);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch messages');
    }
  }
);

export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async ({ conversationId, content }: { conversationId: string; content: string }, { rejectWithValue }) => {
    try {
      logger.log('💬 Redux: Sending message...');
      const { data } = await axiosInstance.post(`${API_URL}/chats/${conversationId}/messages`, { content });
      
      if (data.success) {
        logger.log('✅ Redux: Message sent successfully');
        return data.data.message; // Return just the message object, not the entire data
      }
      return rejectWithValue('Failed to send message');
    } catch (error: any) {
      // Log the error for debugging
      console.error('❌ Redux: Send message error:', error.response?.data?.message || error.message);
      return rejectWithValue(error.response?.data?.message || 'Failed to send message');
    }
  }
);

export const createConversation = createAsyncThunk(
  'chat/createConversation',
  async (participantId: string, { rejectWithValue }) => {
    try {
      logger.log('💬 Redux: Creating conversation with:', participantId);
      const { data } = await axiosInstance.post(`${API_URL}/chat/conversations`, { participantId });
      
      if (data.success) {
        logger.log('✅ Redux: Conversation created successfully');
        return data.data;
      }
      return rejectWithValue('Failed to create conversation');
    } catch (error: any) {
      // Silently handle errors when backend not accessible (demo mode)
      // console.error('❌ Redux: Create conversation error:', error.response?.data?.message);
      return rejectWithValue(error.response?.data?.message || 'Failed to create conversation');
    }
  }
);

export const markAsRead = createAsyncThunk(
  'chat/markAsRead',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.put(`${API_URL}/chats/${conversationId}/read`);
      
      if (data.success) {
        return conversationId;
      }
      return rejectWithValue('Failed to mark as read');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark as read');
    }
  }
);

export const blockUser = createAsyncThunk(
  'chat/blockUser',
  async (userId: string, { rejectWithValue }) => {
    try {
      logger.log('🚫 Redux: Blocking user:', userId);
      const { data } = await axiosInstance.post(`${API_URL}/users/block`, { blockedUserId: userId });
      
      if (data.success) {
        logger.log('✅ Redux: User blocked successfully');
        return userId;
      }
      return rejectWithValue('Failed to block user');
    } catch (error: any) {
      console.error('❌ Redux: Block user error:', error.response?.data?.message || error.message);
      return rejectWithValue(error.response?.data?.message || 'Failed to block user');
    }
  }
);

export const unblockUser = createAsyncThunk(
  'chat/unblockUser',
  async (userId: string, { rejectWithValue }) => {
    try {
      logger.log('✅ Redux: Unblocking user:', userId);
      const { data } = await axiosInstance.post(`${API_URL}/users/unblock`, { unblockedUserId: userId });
      
      if (data.success) {
        logger.log('✅ Redux: User unblocked successfully');
        return userId;
      }
      return rejectWithValue('Failed to unblock user');
    } catch (error: any) {
      console.error('❌ Redux: Unblock user error:', error.response?.data?.message || error.message);
      return rejectWithValue(error.response?.data?.message || 'Failed to unblock user');
    }
  }
);

export const fetchBlockedUsers = createAsyncThunk(
  'chat/fetchBlockedUsers',
  async (_, { rejectWithValue }) => {
    try {
      logger.log('📋 Redux: Fetching blocked users...');
      const { data } = await axiosInstance.get(`${API_URL}/users/blocked`);
      
      if (data.success) {
        logger.log('✅ Redux: Blocked users fetched:', data.data.blockedUsers);
        // Return just the array of user IDs
        return data.data.blockedUsers.map((user: any) => user._id || user);
      }
      return rejectWithValue('Failed to fetch blocked users');
    } catch (error: any) {
      // Silently return empty array on network error
      if (error.message === 'Network Error' || !error.response) {
        logger.log('🎭 Redux: Network error, returning empty blocked users list');
        return [];
      }
      console.error('❌ Redux: Fetch blocked users error:', error.response?.data?.message || error.message);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch blocked users');
    }
  }
);

// Load older messages for pagination (prepend to existing messages)
export const fetchOlderMessages = createAsyncThunk(
  'chat/fetchOlderMessages',
  async ({ conversationId, page, limit = 50 }: { conversationId: string; page: number; limit?: number }, { rejectWithValue }) => {
    try {
      logger.log('📜 Redux: Fetching older messages - Page:', page);
      const { data } = await axiosInstance.get(`${API_URL}/chats/${conversationId}/messages?page=${page}&limit=${limit}`);
      
      if (data.success) {
        logger.log('✅ Redux: Older messages fetched:', data.data.chat?.messages?.length || 0);
        return {
          messages: data.data.chat?.messages || [],
          hasMore: data.data.hasMore !== false,
          page
        };
      }
      return rejectWithValue('Failed to fetch older messages');
    } catch (error: any) {
      console.error('❌ Redux: Fetch older messages error:', error.response?.data?.message);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch older messages');
    }
  }
);

// Slice
const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    clearChatError: (state) => {
      state.error = null;
    },
    setCurrentConversation: (state, action: PayloadAction<Conversation | null>) => {
      state.currentConversation = action.payload;
      // Persist to localStorage so messages persist on page refresh
      if (action.payload) {
        try {
          localStorage.setItem('lastConversationId', action.payload._id);
        } catch (e) {
          logger.error('Failed to save conversation to localStorage:', e);
        }
      } else {
        localStorage.removeItem('lastConversationId');
      }
    },
    addMessage: (state, action: PayloadAction<Message>) => {
      // Check if message already exists (prevent duplicates)
      const messageExists = state.messages.some(msg => msg._id === action.payload._id);
      if (!messageExists) {
        state.messages.push(action.payload);
      } else {
        logger.log('⏭️  Skipping duplicate message:', action.payload._id);
      }
    },
    incrementUnreadCount: (state, action: PayloadAction<string>) => {
      logger.log('🔥🔥🔥 ============================================');
      logger.log('📬 [Redux] incrementUnreadCount ACTION CALLED');
      logger.log('🔥🔥🔥 ============================================');
      logger.log('📬 [Redux] Conversation ID:', action.payload);
      logger.log('📬 [Redux] Current conversations:', state.conversations.map(c => ({ id: c._id, unread: c.unreadCount })));
      
      // Increment unread count for a specific conversation (when message received for non-active chat)
      const conversation = state.conversations.find(c => c._id === action.payload);
      
      if (conversation) {
        const oldCount = conversation.unreadCount || 0;
        // With Immer, we can mutate directly
        conversation.unreadCount = oldCount + 1;
        
        logger.log('📬 [Redux] Incrementing unread count:', { oldCount, newCount: conversation.unreadCount });
        logger.log('✅ [Redux] Unread count incremented successfully!', {
          conversationId: action.payload,
          newCount: conversation.unreadCount,
          totalConversationsWithUnread: state.conversations.filter(c => (c.unreadCount || 0) > 0).length
        });
      } else {
        console.warn('⚠️  [Redux] Conversation not found in state - cannot increment unread count');
      }
      // REMOVED: state.lastUpdate = Date.now(); - unnecessary re-render trigger
    },
    updateConversationLastMessage: (state, action: PayloadAction<{ conversationId: string; message: any }>) => {
      // Update last message in conversations list
      const conversationIndex = state.conversations.findIndex(c => c._id === action.payload.conversationId);
      
      if (conversationIndex !== -1 && action.payload.message) {
        // With Redux Toolkit + Immer, we can mutate directly
        const conversation = state.conversations[conversationIndex];
        
        // Update lastMessage inline
        conversation.lastMessage = {
          content: action.payload.message.content,
          createdAt: action.payload.message.createdAt,
          sender: action.payload.message.senderId
        };
        conversation.updatedAt = action.payload.message.createdAt;
        
        // DON'T move to top here - let component handle sorting
        // This prevents array reference change on every message
        
        logger.log('✅ Conversation list updated:', {
          conversationId: action.payload.conversationId,
          lastMessage: conversation.lastMessage.content,
          timestamp: conversation.updatedAt
        });
      }
      // REMOVED: state.lastUpdate = Date.now(); - unnecessary re-render trigger
    },
    updateOnlineUsers: (state, action: PayloadAction<string[]>) => {
      state.onlineUsers = action.payload;
    },
    addOnlineUser: (state, action: PayloadAction<string>) => {
      if (!state.onlineUsers.includes(action.payload)) {
        state.onlineUsers.push(action.payload);
      }
      // Update user presence
      state.userPresence[action.payload] = {
        isOnline: true,
        lastSeen: new Date().toISOString()
      };
    },
    removeOnlineUser: (state, action: PayloadAction<string>) => {
      state.onlineUsers = state.onlineUsers.filter(id => id !== action.payload);
      // Update user presence with last seen
      if (state.userPresence[action.payload]) {
        state.userPresence[action.payload] = {
          isOnline: false,
          lastSeen: new Date().toISOString()
        };
      }
    },
    updateUserPresence: (state, action: PayloadAction<{ userId: string; isOnline: boolean; lastSeen?: string }>) => {
      const { userId, isOnline, lastSeen } = action.payload;
      state.userPresence[userId] = {
        isOnline,
        lastSeen: lastSeen || new Date().toISOString()
      };
    },
    addNewConversation: (state, action: PayloadAction<Conversation>) => {
      logger.log('💬 [Redux] Adding new conversation:', action.payload._id);
      
      // Check if conversation already exists (prevent duplicates)
      const conversationExists = state.conversations.some(c => c._id === action.payload._id);
      if (!conversationExists) {
        // Add new conversation at the beginning of the list
        state.conversations.unshift(action.payload);
        logger.log('✅ [Redux] New conversation added successfully');
      } else {
        logger.log('⏭️  [Redux] Conversation already exists, skipping');
      }
      // REMOVED: state.lastUpdate = Date.now(); - Immer handles reactivity
    },
    // Optimistic message handling
    addOptimisticMessage: (state, action: PayloadAction<Message>) => {
      state.messages.push(action.payload);
      // REMOVED: state.lastUpdate = Date.now(); - Immer handles reactivity
    },
    removeOptimisticMessage: (state, action: PayloadAction<string>) => {
      const index = state.messages.findIndex(msg => msg._id === action.payload);
      if (index !== -1) {
        state.messages.splice(index, 1);
      }
      // REMOVED: state.lastUpdate = Date.now(); - Immer handles reactivity
    },
    updateMessageId: (state, action: PayloadAction<{ tempId: string; realId: string; message: Message }>) => {
      const messageIndex = state.messages.findIndex(msg => msg._id === action.payload.tempId);
      if (messageIndex !== -1) {
        // Replace temp message with real message from server
        state.messages[messageIndex] = action.payload.message;
        logger.log('✅ [Redux] Updated message ID from temp to real:', action.payload.tempId, '->', action.payload.realId);
      } else {
        console.warn('⚠️ [Redux] Temp message not found, adding real message:', action.payload.realId);
        // If temp message not found, add the real message (safety net)
        state.messages.push(action.payload.message);
      }
      // REMOVED: state.lastUpdate = Date.now(); - Immer handles reactivity
    },
  },
  extraReducers: (builder) => {
    // Fetch conversations
    builder.addCase(fetchConversations.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchConversations.fulfilled, (state, action) => {
      state.loading = false;
      // Only update conversations if we got new data (not from cache)
      if (action.payload !== null) {
        state.conversations = Array.isArray(action.payload) ? action.payload : [];
        state.lastFetched = Date.now();
        state.cacheValid = true;
      }
      // If payload is null, we're using cache, so keep existing conversations
      state.error = null;
    });
    builder.addCase(fetchConversations.rejected, (state, action) => {
      state.loading = false;
      // DON'T clear conversations on error - keep existing ones
      // Only clear if it was the initial fetch (no existing conversations)
      if (state.conversations.length === 0) {
        logger.log('⚠️ [Redux] Initial fetch failed, conversations remain empty');
      } else {
        logger.log('⚠️ [Redux] Fetch failed but keeping existing conversations:', state.conversations.length);
      }
      state.cacheValid = false; // Mark cache as invalid to allow retry
      state.error = action.payload as string;
    });

    // Fetch messages
    builder.addCase(fetchMessages.pending, (state) => {
      state.messagesLoading = true;
      state.error = null;
    });
    builder.addCase(fetchMessages.fulfilled, (state, action) => {
      state.messagesLoading = false;
      // Add defensive check - ensure messages is an array
      state.messages = Array.isArray(action.payload?.messages) ? action.payload.messages : [];
      state.hasMoreMessages = action.payload.hasMore;
      state.messagesPage = action.payload.page;
      state.error = null;
    });
    builder.addCase(fetchMessages.rejected, (state, action) => {
      state.messagesLoading = false;
      // Keep messages as empty array on error
      state.messages = [];
      state.error = action.payload as string;
    });

    // Send message
    builder.addCase(sendMessage.fulfilled, (state, action) => {
      // Message is already handled via optimistic updates and updateMessageId
      // Don't add it again to prevent duplicates
      logger.log('✅ [Redux] Message sent successfully (handled via optimistic update)');
    });
    builder.addCase(sendMessage.rejected, (state, action) => {
      state.error = action.payload as string;
    });

    // Create conversation
    builder.addCase(createConversation.fulfilled, (state, action) => {
      state.conversations.unshift(action.payload);
      state.currentConversation = action.payload;
    });
    builder.addCase(createConversation.rejected, (state, action) => {
      state.error = action.payload as string;
    });

    // Mark as read
    builder.addCase(markAsRead.fulfilled, (state, action) => {
      const conversation = state.conversations.find(c => c._id === action.payload);
      if (conversation) {
        conversation.unreadCount = 0;
      }
    });

    // Block user
    builder.addCase(blockUser.fulfilled, (state, action) => {
      state.blockedUsers.push(action.payload);
      logger.log('✅ [Redux] User blocked, updating state');
    });
    builder.addCase(blockUser.rejected, (state, action) => {
      state.error = action.payload as string;
    });

    // Unblock user
    builder.addCase(unblockUser.fulfilled, (state, action) => {
      state.blockedUsers = state.blockedUsers.filter(id => id !== action.payload);
      logger.log('✅ [Redux] User unblocked, updating state');
    });
    builder.addCase(unblockUser.rejected, (state, action) => {
      state.error = action.payload as string;
    });

    // Fetch blocked users
    builder.addCase(fetchBlockedUsers.fulfilled, (state, action) => {
      state.blockedUsers = action.payload;
      logger.log('✅ [Redux] Blocked users list updated');
    });
    builder.addCase(fetchBlockedUsers.rejected, (state, action) => {
      state.error = action.payload as string;
    });

    // Load older messages
    builder.addCase(fetchOlderMessages.pending, (state) => {
      state.loadingOlderMessages = true;
      state.error = null;
    });
    builder.addCase(fetchOlderMessages.fulfilled, (state, action) => {
      state.loadingOlderMessages = false;
      
      if (Array.isArray(action.payload?.messages)) {
        // Deduplicate messages - only add messages that don't already exist
        const existingMessageIds = new Set(state.messages.map(msg => msg._id));
        const newMessages = action.payload.messages.filter(msg => !existingMessageIds.has(msg._id));
        
        logger.log('📜 Deduplication check:', {
          received: action.payload.messages.length,
          duplicates: action.payload.messages.length - newMessages.length,
          adding: newMessages.length
        });
        
        // Prepend only unique new messages
        state.messages = [...newMessages, ...state.messages];
        
        // If we received 0 new unique messages, there are no more to load
        if (newMessages.length === 0) {
          state.hasMoreMessages = false;
          logger.log('🛑 No new messages received, stopping pagination');
        } else {
          state.hasMoreMessages = action.payload.hasMore;
        }
      } else {
        state.hasMoreMessages = action.payload.hasMore;
      }
      
      state.messagesPage = action.payload.page;
      state.error = null;
    });
    builder.addCase(fetchOlderMessages.rejected, (state, action) => {
      state.loadingOlderMessages = false;
      // Keep messages as empty array on error
      state.error = action.payload as string;
    });

    // Clear chat data on logout
    builder.addCase(logoutUser.fulfilled, (state) => {
      state.conversations = [];
      state.currentConversation = null;
      state.messages = [];
      state.loading = false;
      state.messagesLoading = false;
      state.error = null;
      state.onlineUsers = [];
      state.userPresence = {};
      state.blockedUsers = [];
      state.lastFetched = null;
      state.cacheValid = false;
    });
  },
});

export const {
  clearChatError,
  setCurrentConversation,
  addMessage,
  incrementUnreadCount,
  updateConversationLastMessage,
  updateOnlineUsers,
  addOnlineUser,
  removeOnlineUser,
  updateUserPresence,
  addNewConversation,
  addOptimisticMessage,
  removeOptimisticMessage,
  updateMessageId,
} = chatSlice.actions;

export default chatSlice.reducer;